// ========================================
// BLOG HELPDESK (Index Page)
// ========================================

class CustomBlogHelpdesk extends HTMLElement {
  constructor() {
    super();
    this.searchInput = null;
    this.articles = [];
    this.categories = [];
    this.resultsDropdown = null;
    this.subTopicFilters = null;
    this.activeCategory = null;
    this.activeTag = null;
  }

  connectedCallback() {
    this.initHelpdesk();
  }

  initHelpdesk() {
    // Select elements
    this.searchInput = this.querySelector('#helpdeskSearch');
    this.resultsDropdown = this.querySelector('.ui-autocomplete');
    this.articles = Array.from(this.querySelectorAll('.helpdesk-article'));
    this.categories = Array.from(this.querySelectorAll('.helpdesk-cat'));
    this.subTopicFilters = this.querySelector('#subTopicFilters');

    // Build searchable data
    const searchableData = this.articles.map((article) => ({
      title: article.dataset.title,
      excerpt: article.dataset.excerpt,
      url: article.dataset.url,
      topics: article.dataset.topics.split(','),
      element: article,
    }));

    // Bind event listeners
    this.searchInput.addEventListener('input', (e) => {
      this.showSearchResults(e.target.value, searchableData);
    });

    // Close search dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.resultsDropdown.style.display = 'none';
      }
    });

    this.categories.forEach((cat) =>
      cat.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCategoryClick(cat);
      })
    );

    // Listen for hash changes (back/forward navigation)
    window.addEventListener('hashchange', () => {
      this.loadCategoryFromHash();
    });

    // Load category from hash on initial load
    this.loadCategoryFromHash();
  }

  loadCategoryFromHash() {
    const hash = window.location.hash.slice(1);
    
    if (hash) {
      const matchingCategory = this.categories.find(cat => cat.dataset.topic === hash);
      if (matchingCategory) {
        // Update active state
        this.categories.forEach(cat => cat.classList.remove('active'));
        matchingCategory.classList.add('active');
        
        // Load the category
        this.activeCategory = hash;
        this.activeTag = null;
        this.updateVisibleArticles();
        this.buildSubTopicFilters();
        return;
      }
    }

    // No hash or invalid hash - show first category
    const activeCat = this.querySelector('.helpdesk-cat.active');
    if (activeCat) {
      this.activeCategory = activeCat.dataset.topic;
      this.updateVisibleArticles();
      this.buildSubTopicFilters();
    }
  }

  showSearchResults(query, searchableData) {
    this.resultsDropdown.innerHTML = '';

    if (!query || query.length < 2) {
      this.resultsDropdown.style.display = 'none';
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filteredResults = searchableData.filter((item) => {
      return (
        item.title.toLowerCase().includes(lowerQuery) ||
        item.excerpt.toLowerCase().includes(lowerQuery)
      );
    });

    if (filteredResults.length === 0) {
      this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
      this.resultsDropdown.style.display = 'block';
      return;
    }

    filteredResults.slice(0, 10).forEach((item) => {
      const resultItem = document.createElement('div');
      resultItem.classList.add('result-item');
      resultItem.innerHTML = `<strong>${this.highlightMatch(item.title, query)}</strong>`;
      
      resultItem.addEventListener('click', () => {
        // Navigate to article URL
        window.location.href = item.url;
      });

      this.resultsDropdown.appendChild(resultItem);
    });

    this.resultsDropdown.style.display = 'block';
  }

  highlightMatch(text, query) {
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  navigateToArticle(articleElement) {
    // Get the first topic of this article
    const topics = articleElement.dataset.topics.split(',');
    if (topics[0]) {
      this.activeCategory = topics[0];
      
      // Update active category button
      this.categories.forEach((cat) => {
        cat.classList.toggle('active', cat.dataset.topic === this.activeCategory);
      });

      // Clear tag filter
      this.activeTag = null;
      
      // Update visible articles
      this.updateVisibleArticles();
      this.buildSubTopicFilters();
    }

    // Scroll to article
    window.scrollTo({
      top: articleElement.offsetTop - 120,
      behavior: 'smooth',
    });

    // Clear search
    this.searchInput.value = '';
    this.resultsDropdown.style.display = 'none';
  }

  handleCategoryClick(categoryElement) {
    const topic = categoryElement.dataset.topic;

    // Update active state
    this.categories.forEach((cat) =>
      cat.classList.toggle('active', cat === categoryElement)
    );

    // Reset tag filter when switching categories
    this.activeCategory = topic;
    this.activeTag = null;

    // Update view
    this.updateVisibleArticles();
    this.buildSubTopicFilters();
    
    // Update URL hash
    window.location.hash = topic;

    // Scroll to top of articles
    const articlesSection = this.querySelector('.helpdesk-articles');
    if (articlesSection) {
      window.scrollTo({
        top: articlesSection.offsetTop - 120,
        behavior: 'smooth',
      });
    }
  }

  buildSubTopicFilters() {
    // Clear existing filters
    this.subTopicFilters.innerHTML = '';

    if (!this.activeCategory) return;

    // Get all visible articles
    const visibleArticles = this.articles.filter((article) => {
      const topics = article.dataset.topics.split(',');
      return topics.includes(this.activeCategory);
    });

    // Collect all unique tags from visible articles
    const allTags = new Set();
    visibleArticles.forEach((article) => {
      const tags = article.dataset.tags.split(',').filter(tag => tag.trim() !== '');
      tags.forEach(tag => allTags.add(tag));
    });

    // If there are tags, show the filter section
    if (allTags.size > 0) {
      // Add "All" option
      const allTag = document.createElement('button');
      allTag.classList.add('subtopic-tag');
      if (!this.activeTag) allTag.classList.add('active');
      allTag.textContent = 'All';
      allTag.addEventListener('click', () => this.handleTagClick(null));
      this.subTopicFilters.appendChild(allTag);

      // Add individual tags
      Array.from(allTags).sort().forEach((tag) => {
        const tagButton = document.createElement('button');
        tagButton.classList.add('subtopic-tag');
        if (this.activeTag === tag) tagButton.classList.add('active');
        
        // Convert handleized tag back to readable format
        const readableTag = tag.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        tagButton.textContent = readableTag;
        tagButton.dataset.tag = tag;
        tagButton.addEventListener('click', () => this.handleTagClick(tag));
        this.subTopicFilters.appendChild(tagButton);
      });

      this.subTopicFilters.classList.add('visible');
    } else {
      this.subTopicFilters.classList.remove('visible');
    }
  }

  handleTagClick(tag) {
    this.activeTag = tag;
    
    // Update active state on tag buttons
    const tagButtons = this.subTopicFilters.querySelectorAll('.subtopic-tag');
    tagButtons.forEach((btn) => {
      if (tag === null) {
        btn.classList.toggle('active', btn.textContent === 'All');
      } else {
        btn.classList.toggle('active', btn.dataset.tag === tag);
      }
    });

    // Update visible articles
    this.updateVisibleArticles();
  }

  updateVisibleArticles() {
    this.articles.forEach((article) => {
      const topics = article.dataset.topics.split(',');
      const tags = article.dataset.tags.split(',');
      
      // Check if article matches active category
      const matchesTopic = topics.includes(this.activeCategory);
      
      // Check if article matches active tag (if one is selected)
      const matchesTag = !this.activeTag || tags.includes(this.activeTag);
      
      // Show article if it matches both filters
      article.classList.toggle('visible', matchesTopic && matchesTag);
    });
  }
}

customElements.define('custom-blog-helpdesk', CustomBlogHelpdesk);

// ========================================
// HELPDESK ARTICLE NAV (Individual Article Page)
// ========================================

class HelpdeskArticleNav extends HTMLElement {
  constructor() {
    super();
    this.searchInput = null;
    this.resultsDropdown = null;
    this.blogHandle = null;
  }

  connectedCallback() {
    this.initArticleNav();
  }

  initArticleNav() {
    this.blogHandle = this.dataset.blogHandle;
    this.searchInput = this.querySelector('#helpdeskArticleSearch');
    this.resultsDropdown = this.querySelector('.ui-autocomplete');

    if (!this.searchInput || !this.resultsDropdown) return;

    // Fetch all articles from the blog
    this.fetchBlogArticles();

    // Bind search input
    this.searchInput.addEventListener('input', (e) => {
      this.showSearchResults(e.target.value);
    });

    // Close search dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.resultsDropdown.style.display = 'none';
      }
    });
  }

  async fetchBlogArticles() {
    try {
      const response = await fetch(`/blogs/${this.blogHandle}?view=json`);
      if (!response.ok) {
        // Fallback: try to get articles from the page if JSON view doesn't exist
        this.articlesData = [];
        console.log('JSON view not available, search will be limited');
        return;
      }
      const data = await response.json();
      this.articlesData = data.articles || [];
    } catch (error) {
      console.error('Error fetching blog articles:', error);
      this.articlesData = [];
    }
  }

  showSearchResults(query) {
    this.resultsDropdown.innerHTML = '';

    if (!query || query.length < 2) {
      this.resultsDropdown.style.display = 'none';
      return;
    }

    if (!this.articlesData || this.articlesData.length === 0) {
      this.resultsDropdown.innerHTML = `<div class="result-item">Search not available</div>`;
      this.resultsDropdown.style.display = 'block';
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filteredResults = this.articlesData.filter((article) => {
      return (
        article.title.toLowerCase().includes(lowerQuery) ||
        (article.excerpt && article.excerpt.toLowerCase().includes(lowerQuery))
      );
    });

    if (filteredResults.length === 0) {
      this.resultsDropdown.innerHTML = `<div class="result-item">No results found</div>`;
      this.resultsDropdown.style.display = 'block';
      return;
    }

    filteredResults.slice(0, 10).forEach((article) => {
      const resultItem = document.createElement('div');
      resultItem.classList.add('result-item');
      resultItem.innerHTML = `<strong>${this.highlightMatch(article.title, query)}</strong>`;
      
      resultItem.addEventListener('click', () => {
        window.location.href = article.url;
      });

      this.resultsDropdown.appendChild(resultItem);
    });

    this.resultsDropdown.style.display = 'block';
  }

  highlightMatch(text, query) {
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

customElements.define('helpdesk-article-nav', HelpdeskArticleNav);